import {makeAutoObservable} from 'mobx';

import Ajv from 'ajv';
import ajvErrors from 'ajv-errors';
import addFormats from 'ajv-formats';

import {
    CUSTOMER_SCHEMA,
    QUOTE_BLANK_SCHEMA,
    QUOTE_VUL_SCHEMA,
    QUOTE_RETIREMENT_FOCUSED_SCHEMA,
    QUOTE_BEST_VALUE_SCHEMA,
    QUOTE_BASIC_PERMANENT_SCHEMA,
    QUOTE_TEMPORARY_SCHEMA,
} from './FormSchemas';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import RootStore from '../../../Core/mobx/RootStore';
import {CustomerPortal, CustomerPortalPipelineSteps} from '../../types';

import CustomerPortalAPI from '../../../APIs/CustomerPortalAPI';
import {CarrierOptions} from '../../Policy/SelectOptions';

const QUOTE_SCHEMA: Record<string, any> = {
    '0': QUOTE_BLANK_SCHEMA,
    '1': QUOTE_VUL_SCHEMA,
    '2': QUOTE_RETIREMENT_FOCUSED_SCHEMA,
    '3': QUOTE_BEST_VALUE_SCHEMA,
    '4': QUOTE_BASIC_PERMANENT_SCHEMA,
    '5': QUOTE_TEMPORARY_SCHEMA,
};

const QUOTE_VALUES: Record<string, string> = {
    '1': 'VUL',
    '2': 'Retirement Focused',
    '3': 'Best Value',
    '4': 'Basic Permanent',
    '5': 'Temporary',
};

interface IQuoteCarrier {
    carrier: string;
    options: string[];
}

type CloseFunction = () => void;

export default class CustomerQuoteForm {
    customer = new Map();

    quote = new Map();

    quoteType = 0;

    customerErrors = new Map();

    quoteErrors = new Map();

    quoteCarrier: IQuoteCarrier | undefined;

    savingInProgress: boolean = false;

    reactProps: {open: boolean; close: CloseFunction};

    constructor(props: {open: boolean; close: CloseFunction}) {
        makeAutoObservable(this);
        this.reactProps = props;

        // if existing customer is bound, then prefill the data of it
        const {selectedCustomer} = RootStore.agentPortalStore.customerPortalStore;
        let defaultCustomer: any[string][string];
        if (selectedCustomer) {
            return;
        } else {
            defaultCustomer = this.getCustomerBasicInfoFromPreApproval();
        }
        this.customer = new Map(defaultCustomer);
    }

    getCustomerBasicInfoFromPreApproval(): any[string][string] {
        const approvalParams = RootStore.agentPortalStore.parameterStore.approval;
        const basicInfoParamsToEntries = [
            ['firstName', approvalParams?.firstName],
            ['lastName', approvalParams?.lastName],
            ['email', approvalParams?.emailAddress],
            ['phoneNumber', approvalParams?.phoneNumber],
        ];
        return basicInfoParamsToEntries;
    }

    getQuotePermanentCarrierFromPreApproval(): IQuoteCarrier | undefined {
        const carrier = RootStore.agentPortalStore.parameterStore.approval?.firstCarrierQuoted;

        return carrier && {carrier: carrier?.value, options: ['', ...CarrierOptions]};
    }

    setCustomer(field: string, value: any) {
        this.customer.set(field, value || undefined);
    }

    getCustomer(): CustomerPortal {
        return Object.fromEntries(this.customer);
    }

    getCustomerErrors() {
        return Object.fromEntries(this.customerErrors);
    }

    setQuoteType(type: number) {
        this.quote.clear();
        this.quoteErrors.clear();
        this.quoteType = type;
        this.quoteCarrier = this.getQuotePermanentCarrierFromPreApproval();
        if (type >= 2 && type <= 4 && this.quoteCarrier?.carrier) {
            this.setQuote('user_selected_perm_carrier', this.quoteCarrier?.carrier);
        }
    }

    setQuote(field: string, value: any) {
        this.quote.set(field, value || undefined);
    }

    getQuote() {
        return Object.fromEntries(this.quote);
    }

    getQuoteErrors() {
        return Object.fromEntries(this.quoteErrors);
    }

    feedErrors(errors: any, acc: Map<string, any>) {
        if (!errors) {
            return null;
        }
        errors.forEach((error: any) => {
            if (error.message === 'required-blank') {
                this.feedErrors(error.params.errors, acc);
            }
            if (error.params.missingProperty) {
                acc.set(error.params.missingProperty, "Can't be blank");
            }

            if (error.message === 'invalid-format') {
                this.feedErrors(error.params.errors, acc);
            }
            if (error.keyword === 'format') {
                acc.set(error.instancePath.substring(1), error.message);
            }
        });
    }

    async checkCustomerExist(email: string): Promise<boolean> {
        if (!email) {
            return false;
        }
        const customer = await CustomerPortalAPI.GetCustomerPortalData(email);

        return customer?.customerByEmail?.email === email;
    }

    async saveForm() {
        const customer = this.getCustomer();
        const quote = this.getQuote();

        const ajv = new Ajv({allErrors: true});
        addFormats(ajv);
        ajvErrors(ajv, {singleError: true});

        const validateCustomer = ajv.compile({...CUSTOMER_SCHEMA});
        const validateQuote = ajv.compile(QUOTE_SCHEMA[this.quoteType.toString()]);

        const isValidCustomer = validateCustomer(customer);
        const isValidQuote = validateQuote(quote);

        this.customerErrors.clear();
        this.quoteErrors.clear();

        this.feedErrors(validateCustomer.errors, this.customerErrors);
        this.feedErrors(validateQuote.errors, this.quoteErrors);

        if (!isValidCustomer || !isValidQuote) {
            return;
        }

        const existingCustomerByEmail = await this.checkCustomerExist(
            typeof customer.email === 'string' ? customer.email : '',
        );

        if (existingCustomerByEmail) {
            this.customerErrors.set('email', 'Email address already exist');
            return;
        }

        this.savingInProgress = true;

        const {firstName, lastName, email, phoneNumber} = customer;

        const quoteDetailsProperties = Object.entries({
            user_selected_category: QUOTE_VALUES[this.quoteType],
            ...(quote as Record<string, unknown>),
        }).map((field) => ({property: field[0], value: field[1]}));

        const quoteBody = {
            email,
            quote: {
                properties: quoteDetailsProperties,
            },
        };

        const {customerPortalStore} = RootStore.agentPortalStore;
        const {uiStore} = RootStore;

        await customerPortalStore.createCustomerAndQuote(
            {firstName, lastName, email, phoneNumber, pipelineStep: CustomerPortalPipelineSteps.Walkthrough},
            quoteBody,
            {
                next: (resp) => {
                    if (resp.customer.errors && !resp.quote.errors) {
                        uiStore.showSnackbar('Failed to create customer', 'error');
                    }
                    if (!resp.customer.errors && resp.quote.errors) {
                        uiStore.showSnackbar('Failed to create quote', 'error');
                    }
                    if (!resp.customer.errors && !resp.quote.errors) {
                        uiStore.showSnackbar('Failed to create customer and quote.', 'error');
                    }

                    if (!resp.customer.errors && !resp.quote.errors) {
                        uiStore.showSnackbar('Customer and Quote created successfully.', 'success');

                        this.customer.clear();
                        this.quote.clear();
                        this.reactProps?.close();
                    }
                },
            },
        );

        this.savingInProgress = false;
    }
}
